import { safeUpdateDoc, safeAddDoc } from '../utils';
import { useState, useRef } from 'react';
import ChatView from './ChatView';
import { db } from '../firebase';
import { collection, serverTimestamp, doc } from 'firebase/firestore';
import Avatar from './Avatar';

const FloatingDM = ({
  openDMChannelId,
  dmMessages,
  user,
  profile,
  channels,
  allUsers,
  dispatch,
  getDmUser,
  appId,
  ...props
}) => {
  const [dims, setDims] = useState({ w: 400, h: 550 });
  const [pos, setPos] = useState(() => {
    // Offset slightly if there are multiple, but simple random offset is fine
    const offset = Math.floor(Math.random() * 40);
    return { 
      x: window.innerWidth - 420 - offset, 
      y: window.innerHeight - 570 - offset 
    };
  });
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);

  if (!openDMChannelId) return null;

  const activeChannel = (channels || []).find((c) => c.id === openDMChannelId);
  if (!activeChannel) return null;

  const dmTargetUser = getDmUser
    ? getDmUser(activeChannel)
    : (allUsers || []).find(
        (u) => (activeChannel.participants || []).includes(u.id) && u.id !== user?.uid
      );
  const dmLabel = dmTargetUser?.name || 'Direct Message';
  const isTargetOnline =
    dmTargetUser &&
    dmTargetUser.lastSeen?.seconds * 1000 >
      Date.now() /* eslint-disable-line react-hooks/purity */ - 5 * 60 * 1000;

  const handleSendDM = async (text) => {
    if (!text.trim()) return;
    const uid = user?.uid || 'test-uid-123';
    await safeAddDoc(collection(db, 'artifacts', appId, 'public', 'data', 'messages'), {
      text,
      channelId: openDMChannelId,
      userId: uid,
      senderName: profile?.name || 'User',
      senderAvatar: profile?.avatar || '👤',
      createdAt: serverTimestamp(),
    });
    try {
      await safeUpdateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'channels', openDMChannelId), {
        lastActivity: serverTimestamp(),
        lastActorId: uid,
      });
    } catch (e) {
      // ignore
    }
  };

  const handleClose = () => {
    dispatch({ type: 'CLOSE_DM', payload: openDMChannelId });
  };

  const customHeader = (
    <div className="flex items-center gap-2 min-w-0">
      {dmTargetUser && (
        <button
          onClick={() => props.handleShowProfile?.(dmTargetUser)}
          className="flex items-center gap-2 flex-shrink-0 hover:opacity-80 transition-opacity"
        >
          <Avatar src={dmTargetUser.avatar} size={20} />
          {isTargetOnline && <div className="w-1.5 h-1.5 rounded-full bg-green-500" />}
        </button>
      )}
      <h2
        data-testid="tiki-bar-title"
        className="text-[10px] uppercase tracking-widest font-black text-zinc-800 truncate select-none"
      >
        {dmLabel}
      </h2>
    </div>
  );

  const startDrag = (e) => {
    if (e.target.closest('button')) return;
    e.preventDefault();
    setIsDragging(true);
    const startX = e.clientX;
    const startY = e.clientY;
    const startPosX = pos.x;
    const startPosY = pos.y;

    const onMouseMove = (ev) => {
      setPos({
        x: startPosX + (ev.clientX - startX),
        y: Math.max(0, startPosY + (ev.clientY - startY)), // don't let it go above top of screen
      });
    };

    const onMouseUp = () => {
      setIsDragging(false);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  const startResize = (e, edges) => {
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    const startW = dims.w;
    const startH = dims.h;
    const startPosX = pos.x;
    const startPosY = pos.y;

    const onMouseMove = (ev) => {
      let newW = startW;
      let newH = startH;
      let newX = startPosX;
      let newY = startPosY;

      if (edges.includes('left')) {
        const diff = startX - ev.clientX;
        newW = startW + diff;
        if (newW > 320) newX = startPosX - diff;
      }
      if (edges.includes('right')) {
        newW = startW + (ev.clientX - startX);
      }
      if (edges.includes('top')) {
        const diff = startY - ev.clientY;
        newH = startH + diff;
        if (newH > 400) newY = startPosY - diff;
      }
      if (edges.includes('bottom')) {
        newH = startH + (ev.clientY - startY);
      }

      setDims({
        w: Math.max(320, newW),
        h: Math.max(400, newH),
      });
      setPos({ x: newX, y: newY });
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  return (
    <div
      ref={containerRef}
      className="fixed bg-white shadow-2xl rounded-xl border border-zinc-200 flex flex-col z-50 overflow-hidden animate-in slide-in-from-bottom-4 duration-300 pointer-events-auto"
      style={{ width: `${dims.w}px`, height: `${dims.h}px`, left: `${pos.x}px`, top: `${pos.y}px` }}
    >
      <div className="flex-1 min-h-0 flex flex-col relative">
        {isDragging ? (
          <div className="flex-1 flex flex-col items-center justify-center bg-zinc-50 pointer-events-none">
            <Avatar src={dmTargetUser?.avatar} size={48} className="mb-4 opacity-50 grayscale" />
            <span className="text-zinc-400 font-black tracking-widest uppercase text-xs">
              {dmLabel}
            </span>
          </div>
        ) : (
          <ChatView
            {...props}
            isFullWidth={true}
            activeChannelId={openDMChannelId}
            messages={dmMessages}
            handleSendMessage={handleSendDM}
            user={user}
            profile={profile}
            channels={channels}
            allUsers={allUsers}
            dispatch={dispatch}
            getDmUser={getDmUser}
            isDM={true}
            customHeader={customHeader}
            onClose={handleClose}
            appId={appId}
            onHeaderMouseDown={startDrag}
          />
        )}
      </div>

      {/* Resize Handles */}
      <div className="absolute top-0 left-0 w-4 h-4 cursor-nwse-resize z-50 flex items-start justify-start p-0.5 opacity-20 hover:opacity-100 transition-opacity" onMouseDown={(e) => startResize(e, ['top', 'left'])}>
        <div className="w-1.5 h-1.5 border-l-2 border-t-2 border-zinc-500 rounded-sm" />
      </div>
      <div className="absolute top-0 right-0 w-4 h-4 cursor-nesw-resize z-50 flex items-start justify-end p-0.5 opacity-20 hover:opacity-100 transition-opacity" onMouseDown={(e) => startResize(e, ['top', 'right'])}>
        <div className="w-1.5 h-1.5 border-r-2 border-t-2 border-zinc-500 rounded-sm" />
      </div>
      <div className="absolute bottom-0 left-0 w-4 h-4 cursor-nesw-resize z-50 flex items-end justify-start p-0.5 opacity-20 hover:opacity-100 transition-opacity" onMouseDown={(e) => startResize(e, ['bottom', 'left'])}>
        <div className="w-1.5 h-1.5 border-l-2 border-b-2 border-zinc-500 rounded-sm" />
      </div>
      <div className="absolute bottom-0 right-0 w-4 h-4 cursor-nwse-resize z-50 flex items-end justify-end p-0.5 opacity-20 hover:opacity-100 transition-opacity" onMouseDown={(e) => startResize(e, ['bottom', 'right'])}>
        <div className="w-1.5 h-1.5 border-r-2 border-b-2 border-zinc-500 rounded-sm" />
      </div>

      <div className="absolute top-0 left-4 right-4 h-1.5 cursor-ns-resize z-50 opacity-0 hover:opacity-100 bg-blue-500 transition-opacity" onMouseDown={(e) => startResize(e, ['top'])} />
      <div className="absolute bottom-0 left-4 right-4 h-1.5 cursor-ns-resize z-50 opacity-0 hover:opacity-100 bg-blue-500 transition-opacity" onMouseDown={(e) => startResize(e, ['bottom'])} />
      <div className="absolute top-4 bottom-4 left-0 w-1.5 cursor-ew-resize z-50 opacity-0 hover:opacity-100 bg-blue-500 transition-opacity" onMouseDown={(e) => startResize(e, ['left'])} />
      <div className="absolute top-4 bottom-4 right-0 w-1.5 cursor-ew-resize z-50 opacity-0 hover:opacity-100 bg-blue-500 transition-opacity" onMouseDown={(e) => startResize(e, ['right'])} />
    </div>
  );
};

export default FloatingDM;
